import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';

export function DashboardSidebar({ tabs, activeTab, onTabChange, className }) {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved === 'true';
  });
  
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', isCollapsed);
  }, [isCollapsed]);

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };
  
  const toggleMobile = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  return (
    <>
      {/* Bouton hamburger mobile uniquement */}
      <button
        onClick={toggleMobile}
        className="fixed top-20 left-4 z-[60] lg:hidden bg-white p-3 rounded-xl shadow-lg border border-slate-200 hover:bg-slate-50 transition-all"
        aria-label="Toggle menu"
      >
        {isMobileOpen ? (
          <X className="h-6 w-6 text-slate-700" />
        ) : (
          <Menu className="h-6 w-6 text-slate-700" />
        )}
      </button>
      
      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={toggleMobile}
        />
      )}

      {/* Sidebar Professionnel */}
      <aside
        className={cn(
          "fixed left-0 top-[73px] h-[calc(100vh-73px)] bg-gradient-to-b from-slate-50 to-white border-r border-slate-200 shadow-xl transition-all duration-300 z-50 flex flex-col",
          // Desktop : collapse/expand normal
          "lg:translate-x-0",
          isCollapsed ? "lg:w-20" : "lg:w-72",
          // Mobile : hidden par défaut, slide in quand ouvert
          isMobileOpen ? "translate-x-0 w-72" : "-translate-x-full lg:translate-x-0",
          className
        )}
      >
        {/* Header avec Toggle */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-white/80 backdrop-blur-sm">
          {!isCollapsed && (
            <div className="flex items-center gap-3">
              <div className="w-2 h-8 bg-gradient-to-b from-coppam-blue to-coppam-gold rounded-full"></div>
              <div>
                <h2 className="text-sm font-bold text-slate-800 tracking-tight">Navigation</h2>
                <p className="text-xs text-slate-500">Accès rapide</p>
              </div>
            </div>
          )}
          <button
            onClick={toggleSidebar}
            className="p-2.5 hover:bg-slate-100 rounded-xl transition-all hover:shadow-md group ml-auto"
            aria-label={isCollapsed ? 'Ouvrir le menu' : 'Fermer le menu'}
          >
            {isCollapsed ? (
              <ChevronRight className="h-5 w-5 text-slate-600 group-hover:text-coppam-blue transition-colors" />
            ) : (
              <ChevronLeft className="h-5 w-5 text-slate-600 group-hover:text-coppam-blue transition-colors" />
            )}
          </button>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          <ul className="space-y-1">
            {tabs.map((tab, _index) => (
              <li key={tab.value}>
                {tab.isSeparator ? (
                  <>
                    {!isCollapsed && (
                      <div className={cn(
                        "px-4 py-2 text-xs font-bold uppercase tracking-wider mt-4 mb-2",
                        tab.isHighlight 
                          ? "text-emerald-600 bg-gradient-to-r from-emerald-50 to-transparent rounded-lg border-l-4 border-emerald-500" 
                          : "text-slate-500"
                      )}>
                        {tab.label}
                      </div>
                    )}
                    {isCollapsed && tab.isHighlight && (
                      <div className="w-10 h-1 mx-auto my-3 bg-gradient-to-r from-emerald-500 to-blue-500 rounded-full"></div>
                    )}
                  </>
                ) : tab.isLink ? (
                  <a
                    href={tab.path}
                    className={cn(
                      "w-full flex items-center gap-4 px-4 py-3.5 rounded-xl transition-all duration-200 group relative overflow-hidden",
                      tab.isSubItem && "ml-2 py-2.5",
                      "text-slate-700 hover:bg-white hover:shadow-md hover:scale-[1.01]"
                    )}
                    title={isCollapsed ? tab.label : undefined}
                  >
                    {/* Icône */}
                    {tab.icon && (
                      <span className={cn(
                        "flex-shrink-0 transition-transform duration-200",
                        isCollapsed ? "mx-auto scale-110" : "scale-100 group-hover:scale-110",
                        "text-coppam-blue"
                      )}>
                        {React.cloneElement(tab.icon, { 
                          className: cn(
                            tab.icon.props.className,
                            isCollapsed ? "h-6 w-6" : "h-5 w-5"
                          )
                        })}
                      </span>
                    )}
                    
                    {/* Label */}
                    {!isCollapsed && (
                      <span className="text-sm font-semibold truncate flex-1 text-left">
                        {tab.label}
                      </span>
                    )}

                    {/* Effet hover */}
                    <div className="absolute inset-0 bg-gradient-to-r from-coppam-blue/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                  </a>
                ) : (
                  <button
                    onClick={() => {
                      onTabChange(tab.value);
                      // Fermer le menu mobile après sélection
                      setIsMobileOpen(false);
                    }}
                    className={cn(
                      "w-full flex items-center gap-4 px-4 py-3.5 rounded-xl transition-all duration-200 group relative overflow-hidden",
                      tab.isSubItem && "ml-2 py-2.5",
                      // Style spécial pour les éléments financiers
                      tab.isFinance && activeTab !== tab.value && "bg-gradient-to-r from-slate-900/5 to-emerald-500/5 border border-slate-200/50 hover:border-emerald-300 hover:shadow-emerald-100",
                      activeTab === tab.value
                        ? tab.isFinance 
                          ? "bg-gradient-to-r from-emerald-600 via-teal-600 to-blue-600 text-white shadow-lg shadow-emerald-500/30 scale-[1.02] border-0"
                          : "bg-gradient-to-r from-coppam-blue to-blue-600 text-white shadow-lg shadow-coppam-blue/30 scale-[1.02]"
                        : "text-slate-700 hover:bg-white hover:shadow-md hover:scale-[1.01]"
                    )}
                    title={isCollapsed ? tab.label : undefined}
                  >
                    {/* Indicateur actif */}
                    {activeTab === tab.value && !isCollapsed && (
                      <div className={cn(
                        "absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-r-full",
                        tab.isFinance ? "bg-yellow-400" : "bg-coppam-gold"
                      )}></div>
                    )}
                    
                    {/* Effet brillant pour finance */}
                    {tab.isFinance && activeTab === tab.value && (
                      <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 animate-pulse"></div>
                    )}
                    
                    {/* Icône */}
                    {tab.icon && (
                      <span className={cn(
                        "flex-shrink-0 transition-transform duration-200",
                        isCollapsed ? "mx-auto scale-110" : "scale-100 group-hover:scale-110",
                        activeTab === tab.value ? "" : tab.isFinance ? "text-emerald-600" : "text-coppam-blue"
                      )}>
                        {React.cloneElement(tab.icon, { 
                          className: cn(
                            tab.icon.props.className,
                            isCollapsed ? "h-6 w-6" : "h-5 w-5"
                          )
                        })}
                      </span>
                    )}
                    
                    {/* Label */}
                    {!isCollapsed && (
                      <span className={cn(
                        "text-sm font-semibold truncate flex-1 text-left",
                        tab.isFinance && activeTab !== tab.value && "text-slate-800"
                      )}>
                        {tab.label}
                      </span>
                    )}

                    {/* Badge (optionnel) */}
                    {!isCollapsed && tab.badge && (
                      <span className={cn(
                        "px-2 py-0.5 rounded-full text-xs font-bold",
                        activeTab === tab.value 
                          ? "bg-white/20 text-white" 
                          : "bg-coppam-blue/10 text-coppam-blue"
                      )}>
                        {tab.badge}
                      </span>
                    )}

                    {/* Effet hover */}
                    {activeTab !== tab.value && !tab.isFinance && (
                      <div className="absolute inset-0 bg-gradient-to-r from-coppam-blue/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                    )}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </nav>

        {/* Footer professionnel */}
        {!isCollapsed && (
          <div className="p-4 border-t border-slate-200 bg-gradient-to-r from-slate-50 to-white">
            <div className="flex items-center gap-3 text-xs text-slate-600">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-coppam-blue to-coppam-gold flex items-center justify-center text-white font-bold shadow-md">
                C
              </div>
              <div className="flex-1">
                <p className="font-semibold text-slate-800">COPPAM</p>
                <p className="text-slate-500">Espace sécurisé</p>
              </div>
            </div>
          </div>
        )}

        {/* Badge version collapsed */}
        {isCollapsed && (
          <div className="p-3 border-t border-slate-200">
            <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-br from-coppam-blue to-coppam-gold flex items-center justify-center text-white font-bold shadow-lg">
              C
            </div>
          </div>
        )}
      </aside>

      {/* Content offset - invisible spacer (desktop only) */}
      <div
        className={cn(
          "hidden lg:block transition-all duration-300",
          isCollapsed ? "w-16" : "w-64"
        )}
      />
    </>
  );
}

export function DashboardContent({ children, className }) {
  return (
    <div className={cn("flex-1 min-w-0", className)}>
      {children}
    </div>
  );
}
